import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Formik, Form, connect as connectFormik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';

import LeaderboardFormFields, { validateLeaderboardForm, DuplicateWarning } from '../components/LeaderboardFormFields';

import Button from '../../../shared/_TBComponents/Button';
import Spinner from '../../../shared/_TBComponents/Spinner';
import { showErrorToast } from '../../../shared/utils/toastManager';

import {
  fetchExercises,
  editLeaderboard,
  updateTemplate,
} from '../ducks/leaderboardActions';
import { findDuplicateTemplate, normalizeCategory, resolveSubmitGroups } from '../helpers';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Header = styled('div')`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 24px 24px 0;
  flex-shrink: 0;
`;

const Title = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 600;
  color: #444444;
`;

const Subtitle = styled('span')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 400;
  color: #9e9e9e;
`;

const FormSection = styled('div')`
  flex: 1;
  padding: 24px;

  form {
    padding-bottom: 0;
  }
`;

const SpinnerOverlay = styled('div')`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const SaveEditsRow = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 6px;
  margin-bottom: 16px;
`;

const SaveEditsLabel = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 13px;
  font-weight: 700;
  color: #444;
`;

const SaveEditsCheckboxRow = styled('label')`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: ${(props) => (props.isDisabled ? 'default' : 'pointer')};
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: ${(props) => (props.isDisabled ? '#bbb' : '#555')};
  line-height: 1.4;

  input[type='checkbox'] {
    margin-top: 2px;
    flex-shrink: 0;
    width: 15px;
    height: 15px;
    cursor: ${(props) => (props.isDisabled ? 'default' : 'pointer')};
  }
`;

const ErrorText = styled('p')`
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: #c62828;
  margin: 16px 0 0;
`;

const ButtonContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
`;

const buttonTextProps = { fontWeight: '600', letterSpacing: '0px' };

const formMatchesSource = (values, tmpl) => {
  if (!tmpl) return false;
  const groupIds = (arr) => [...(arr || [])].map((g) => g.id).sort().join(',');
  const dateStr = (d) => `${d?.startDate ?? ''}|${d?.endDate ?? ''}`;
  return (
    normalizeCategory(values.category) === normalizeCategory(tmpl.category)
    && values.exerciseId === tmpl.exerciseId
    && values.mustBeInAllGroups === tmpl.mustBeInAllGroups
    && Number(values.gender) === Number(tmpl.gender)
    && values.rangeType === tmpl.rangeType
    && values.compareTo === tmpl.compareTo
    && groupIds(values.groups) === groupIds(tmpl.groups)
    && dateStr(values.rangeDates) === dateStr(tmpl.rangeDates)
    && dateStr(values.compareDates) === dateStr(tmpl.compareDates)
  );
};

// Resets the overwrite checkbox whenever the form returns to matching the saved template.
// Must live inside Formik's tree; connectFormik injects formik.values (Formik v1 equivalent
// of useFormikContext).
const SaveEditsResetterInner = ({
  formik, savedTemplate, setSaveEdits, allGroups,
}) => {
  const resolvedValues = {
    ...formik.values,
    groups: resolveSubmitGroups(formik.values.groups, allGroups),
  };
  const matchesSource = formMatchesSource(resolvedValues, savedTemplate);
  useEffect(() => {
    if (matchesSource) setSaveEdits(false);
  }, [matchesSource, setSaveEdits]);
  return null;
};
SaveEditsResetterInner.propTypes = {
  formik: PropTypes.shape({ values: PropTypes.instanceOf(Object).isRequired }).isRequired,
  savedTemplate: PropTypes.instanceOf(Object).isRequired,
  setSaveEdits: PropTypes.func.isRequired,
  allGroups: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};
const SaveEditsResetter = connectFormik(SaveEditsResetterInner);

const EditLeaderboardModal = ({
  editContext, entityId, closeModal, boardNumber,
}) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const exercises = useSelector((state) => state.leaderboard.data.exercises);
  const groups = useSelector((state) => state.leaderboard.data.groups);
  const isLoadingExercises = useSelector((state) => state.leaderboard.ui.isLoadingExercises);
  const isLoadingGroups = useSelector((state) => state.leaderboard.ui.isLoadingGroups);
  const isLoadingLeaderboard = useSelector((state) => state.leaderboard.ui.isLoadingLeaderboard);
  const isTemplateSavingDeleting = useSelector(
    (state) => state.leaderboard.ui.isTemplateSavingDeleting,
  );
  const exercisesError = useSelector((state) => state.leaderboard.data.exercisesError);
  const leaderboardError = useSelector((state) => state.leaderboard.data.leaderboardError);
  const updateTemplateError = useSelector((state) => state.leaderboard.data.updateTemplateError);
  const templates = useSelector((state) => state.leaderboard.data.templates);
  const leaderboards = useSelector((state) => state.leaderboard.data.leaderboards);
  const dispatch = useDispatch();
  const [saveEdits, setSaveEdits] = useState(false);

  const { accountCode } = currentUser;

  const isTemplateEdit = editContext === 'template';
  const board = !isTemplateEdit ? leaderboards[entityId] : null;
  const template = isTemplateEdit ? templates.find((t) => t.id === entityId) : null;
  const sourceForm = isTemplateEdit ? template : board?.leaderboardForm;
  const boardIsTemplate = !isTemplateEdit && board?.isTemplate;
  const boardTemplateId = !isTemplateEdit && board?.templateId;
  const savedTemplate = boardIsTemplate
    ? templates.find((t) => t.id === boardTemplateId) || null
    : null;
  const modalTitle = isTemplateEdit ? 'Edit Template' : 'Edit Leaderboard';

  useEffect(() => {
    if (sourceForm?.category) {
      dispatch(fetchExercises(accountCode, normalizeCategory(sourceForm.category)));
    }
  }, []);

  if (!sourceForm) return null;

  const initialValues = {
    accountCode,
    category: normalizeCategory(sourceForm.category) || '',
    exerciseId: sourceForm.exerciseId || 0,
    exerciseName: sourceForm.exerciseName || '',
    groups: sourceForm.groups || [],
    mustBeInAllGroups: sourceForm.mustBeInAllGroups || false,
    gender: sourceForm.gender !== undefined ? sourceForm.gender : 0,
    rangeType: sourceForm.rangeType !== undefined ? sourceForm.rangeType : 5,
    rangeDates: sourceForm.rangeDates || {},
    compareTo: sourceForm.compareTo || 0,
    compareDates: sourceForm.compareDates || {},
    saveToTemplate: false,
  };

  const handleSubmit = async (values) => {
    const resolvedValues = { ...values, groups: resolveSubmitGroups(values.groups, groups) };

    if (isTemplateEdit) {
      try {
        const result = await dispatch(updateTemplate(entityId, resolvedValues));
        if (result) {
          closeModal(); // only a truthy result means it actually saved (not a handled 403)
        }
      } catch (err) {
        // updateTemplateError set in Redux via UPDATE_TEMPLATE_REJECTED
      }
      return;
    }

    if (boardIsTemplate && saveEdits && !formMatchesSource(resolvedValues, savedTemplate)) {
      let templateUpdateResult;
      try {
        templateUpdateResult = await dispatch(
          updateTemplate(boardTemplateId, resolvedValues, entityId),
        );
      } catch (err) {
        return; // updateTemplateError set in Redux via UPDATE_TEMPLATE_REJECTED
      }
      if (!templateUpdateResult) {
        return; // 403 fully handled inside updateTemplate
      }
      try {
        await dispatch(editLeaderboard(resolvedValues, entityId));
        // action closes modal and updates board on success
      } catch (err) {
        closeModal();
        showErrorToast(
          err?.response?.headers?.message
          || 'Template saved, but the leaderboard failed to reload. Please try again.',
        );
      }
      return;
    }

    try {
      await dispatch(editLeaderboard(resolvedValues, entityId));
      // action closes modal and updates board on success
    } catch (err) {
      // leaderboardError set in Redux via FETCH_LEADERBOARD_REJECTED
    }
  };

  return (
    <Container>
      <Header>
        <Title>{modalTitle}</Title>
        {boardNumber > 0 && <Subtitle>{`(Leaderboard ${boardNumber})`}</Subtitle>}
      </Header>

      <FormSection>
        {isLoadingLeaderboard ? (
          <SpinnerOverlay><Spinner /></SpinnerOverlay>
        ) : (
          <Formik
            initialValues={initialValues}
            validate={validateLeaderboardForm}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {(formProps) => {
              const resolvedFormValues = {
                ...formProps.values,
                groups: resolveSubmitGroups(formProps.values.groups, groups),
              };
              const matchesSource = formMatchesSource(resolvedFormValues, savedTemplate);
              const effectiveOverwrite = boardIsTemplate && saveEdits && !matchesSource;
              const displayError = (isTemplateEdit || effectiveOverwrite)
                ? updateTemplateError
                : leaderboardError;
              const duplicateExcludeId = isTemplateEdit ? entityId : (boardTemplateId || null);
              const duplicate = (isTemplateEdit || effectiveOverwrite)
                ? findDuplicateTemplate(resolvedFormValues, templates, duplicateExcludeId)
                : null;
              const matchesOriginal = formMatchesSource(resolvedFormValues, sourceForm);
              const noRealChange = matchesOriginal && !effectiveOverwrite;
              return (
                <Form>
                  <LeaderboardFormFields
                    exercises={exercises}
                    exercisesError={exercisesError}
                    groups={groups}
                    formProps={formProps}
                    isLoadingExercises={isLoadingExercises}
                    isLoadingGroups={isLoadingGroups}
                    onCategoryChange={(category) => dispatch(fetchExercises(accountCode, category))}
                  />

                  {!isTemplateEdit && boardIsTemplate && (
                    <SaveEditsRow>
                      {savedTemplate && (
                        <SaveEditsResetter
                          savedTemplate={savedTemplate}
                          setSaveEdits={setSaveEdits}
                          allGroups={groups}
                        />
                      )}
                      <SaveEditsLabel>Overwrite saved template information</SaveEditsLabel>
                      <SaveEditsCheckboxRow isDisabled={matchesSource}>
                        <input
                          type='checkbox'
                          checked={saveEdits}
                          disabled={matchesSource}
                          onChange={(e) => setSaveEdits(e.target.checked)}
                        />
                        Checking this box will overwrite the saved template information.
                        Otherwise, these changes will only apply while viewing this leaderboard.
                      </SaveEditsCheckboxRow>
                    </SaveEditsRow>
                  )}

                  {displayError && <ErrorText>{displayError}</ErrorText>}
                  {duplicate && <DuplicateWarning />}
                  <ButtonContainer>
                    <Button
                      name={isTemplateEdit ? 'Update Leaderboard' : 'Save Changes and View'}
                      bgColor='#444444'
                      txtColor='white'
                      noBorder
                      fullWidth
                      fontSize='14px'
                      customBorderRadius='4px'
                      styledTextProps={buttonTextProps}
                      disabled={
                        isLoadingLeaderboard
                        || isTemplateSavingDeleting
                        || noRealChange
                      }
                      type='submit'
                    />
                    <Button
                      name='Cancel'
                      bgColor='white'
                      borderColor='#444444'
                      txtColor='#444444'
                      fullWidth
                      fontSize='14px'
                      customBorderRadius='4px'
                      styledTextProps={buttonTextProps}
                      onClick={closeModal}
                      disabled={isLoadingLeaderboard || isTemplateSavingDeleting}
                      type='button'
                    />
                  </ButtonContainer>
                </Form>
              );
            }}
          </Formik>
        )}
      </FormSection>
    </Container>
  );
};

EditLeaderboardModal.defaultProps = {
  boardNumber: 0,
};

EditLeaderboardModal.propTypes = {
  editContext: PropTypes.oneOf(['template', 'board']).isRequired,
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  closeModal: PropTypes.func.isRequired,
  boardNumber: PropTypes.number,
};

export default EditLeaderboardModal;
